import { createContext, useContext } from "react";

import { CHAT_PHASE } from "./hooks/useAIChatPhase";

/**
 * Context shape for the v3 AI chat component tree.
 *
 * All lifecycle state derives from `chatPhase` — no independent booleans.
 * Consumers check `chatPhase` directly rather than using derived flags.
 */
export const AIChatV3Context = createContext({
  // -- Lifecycle (state machine) --
  chatPhase: CHAT_PHASE.IDLE,
  /** Derived: true when idle or done. */
  isComplete: true,
  /** Derived: true while a request/build is in flight (!isComplete). */
  isStreaming: false,

  // -- Dashboard pipeline (data-driven builds) --
  pipelineSteps: {},

  // -- Pane extras portal slots (page-scoped content rendered into the pane) --
  tabBarNode: null,
  setTabBarNode: () => {},
  panelNode: null,
  setPanelNode: () => {},

  // -- Chat data --
  chatId: null,
  messages: [],
  previousMessages: [],

  // -- Streaming --
  toolsUsedCount: 0,
  currentToolLabel: null,
  /** LOCAL / support: last completed stream duration + end reason. */
  lastStreamStats: null,

  // -- Input --
  question: "",
  setQuestion: () => {},
  editingMessageId: null,

  // -- Actions --
  onSendQuestion: () => {},
  onRetry: () => {},
  onCancel: () => {},
  onEditLastMessage: () => {},
  onCancelEdit: () => {},
  onSessionClear: () => {},
  canRetryOrEdit: false,

  // -- Chat management --
  onStartNewChat: () => {},
  onSelectChat: () => {},
  chats: [],
  chatsLoading: false,

  // -- Feedback --
  onSubmitMessageFeedback: async () => {},

  // -- Agent selection (support users only) --
  selectedAgent: "ask_teamform_v2",
  setSelectedAgent: () => {},
  isSupportUser: false,

  // -- Ask TeamForm help mode (Workspace / Help toggle) --
  askTeamformMode: "data",
  setAskTeamformMode: () => {},
  enableSupportQuestionInAskTeamform: false,

  // -- Error --
  error: null,
  setError: () => {},

  // -- Scroll --
  setUserHasScrolledUp: () => {},
  userHasScrolledUp: false,
});

export const useAIChatV3 = () => useContext(AIChatV3Context);
